import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import {
  Search,
  ArrowLeft,
  Users,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  MapPin,
  DollarSign,
  Truck,
  Phone,
  Package,
  Star,
  TrendingUp,
  Plus,
  ChevronDown,
  Info,
  Trash2,
  UserPlus,
  UserMinus,
  Activity,
  BarChart,
  Target
} from 'lucide-react'

interface Manager {
  id: string
  full_name: string | null
  email: string | null
  region: string | null
  status: string
  created_at: string
  delivery_partners: {
    id: string
    status: string
    applicant_name: string
    applicant_email: string
    vehicle_type: string
    delivery_zone: string
    total_deliveries: number
    rating: number
  }[]
  active_missions?: number
  total_missions?: number
  success_rate?: number
  performance_score?: number
}

interface DeliveryPartner {
  id: string
  applicant_name: string
  applicant_email: string
  vehicle_type: string
  delivery_zone: string
  status: string
  manager_id: string | null
  total_deliveries: number
  rating: number
}

interface UserProfile {
  id: string
  full_name: string | null
  email: string | null
  role: string
  status: string
}

export function ManagerDeptPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [managers, setManagers] = useState<Manager[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [showAddManager, setShowAddManager] = useState(false)
  const [newManager, setNewManager] = useState({
    userId: '',
    region: ''
  })
  const [selectedManager, setSelectedManager] = useState<Manager | null>(null)
  const [showManagerDetails, setShowManagerDetails] = useState(false)
  const [showAssignPartner, setShowAssignPartner] = useState(false)
  const [availablePartners, setAvailablePartners] = useState<DeliveryPartner[]>([])
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [availableUsers, setAvailableUsers] = useState<UserProfile[]>([])
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [loadingUsers, setLoadingUsers] = useState(false)

  useEffect(() => {
    const ADMIN_EMAILS = ['509aem@gmail.com', 'aemfamily509@gmail.com']
    if (!user?.email || !ADMIN_EMAILS.includes(user.email)) {
      navigate('/')
      return
    }

    fetchManagers()
  }, [user, navigate])

  useEffect(() => {
    if (showAddManager) {
      fetchAvailableUsers()
    }
  }, [showAddManager, userSearchQuery])

  const fetchManagers = async () => {
    try {
      setLoading(true)
      const { data: managers, error: managersError } = await supabase
        .from('profiles')
        .select(`
          *,
          delivery_partners!delivery_partners_manager_id_fkey (
            id,
            status,
            applicant_name,
            applicant_email,
            vehicle_type,
            delivery_zone,
            total_deliveries,
            rating
          )
        `)
        .eq('role', 'manager')
        .order('created_at', { ascending: false })

      if (managersError) throw managersError

      // Get active missions count and calculate performance metrics for each manager
      const managersWithStats = await Promise.all((managers || []).map(async (manager) => {
        const partnerIds = manager.delivery_partners?.map(p => p.id) || []
        
        if (partnerIds.length === 0) {
          return {
            ...manager,
            active_missions: 0,
            total_missions: 0,
            success_rate: 0,
            performance_score: 0
          }
        }

        // Get active missions
        const { count: activeMissions } = await supabase
          .from('delivery_requests')
          .select('*', { count: 'exact' })
          .in('delivery_partner_id', partnerIds)
          .in('current_step', ['pending', 'accepted', 'preparing', 'pickup_ready', 'picked_up', 'in_transit', 'arrived'])

        // Get total missions
        const { data: allMissions } = await supabase
          .from('delivery_requests')
          .select('current_step')
          .in('delivery_partner_id', partnerIds)

        const totalMissions = allMissions?.length || 0
        const completedMissions = allMissions?.filter(m => m.current_step === 'delivered').length || 0
        const successRate = totalMissions > 0 ? (completedMissions / totalMissions) * 100 : 0

        // Calculate performance score based on multiple factors
        const avgRating = manager.delivery_partners?.reduce((sum, p) => sum + (p.rating || 0), 0) / (manager.delivery_partners?.length || 1)
        const performanceScore = (
          (successRate * 0.4) + // 40% weight on success rate
          (avgRating * 20) + // 40% weight on average rating (multiplied by 20 to convert 0-5 to 0-100)
          (Math.min(totalMissions, 100)) * 0.2 // 20% weight on total missions (capped at 100)
        )

        return {
          ...manager,
          active_missions: activeMissions || 0,
          total_missions: totalMissions,
          success_rate: successRate,
          performance_score: performanceScore
        }
      }))

      setManagers(managersWithStats)
    } catch (err) {
      console.error('Error fetching managers:', err)
      setToastMessage('Erreur lors du chargement des managers')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailableUsers = async () => {
    try {
      setLoadingUsers(true)
      let query = supabase
        .from('profiles')
        .select('id, full_name, email, role, status')
        .order('created_at', { ascending: false })

      if (userSearchQuery) {
        query = query.or(`full_name.ilike.%${userSearchQuery}%,email.ilike.%${userSearchQuery}%`)
      }

      const { data, error } = await query

      if (error) throw error
      setAvailableUsers(data || [])
    } catch (err) {
      console.error('Error fetching available users:', err)
      setToastMessage('Erreur lors du chargement des utilisateurs disponibles')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoadingUsers(false)
    }
  }

  const fetchAvailablePartners = async () => {
    try {
      const { data, error } = await supabase
        .from('delivery_partners')
        .select('*')
        .eq('status', 'active')
        .is('manager_id', null)
        .order('created_at', { ascending: false })

      if (error) throw error
      setAvailablePartners(data || [])
    } catch (err) {
      console.error('Error fetching available partners:', err)
      setToastMessage('Erreur lors du chargement des livreurs disponibles')
      setToastType('error')
      setShowToast(true)
    }
  }

  const handleAddManager = async () => {
    try {
      setLoading(true)

      if (!newManager.userId || !newManager.region) {
        setToastMessage('Veuillez sélectionner un utilisateur et spécifier une région')
        setToastType('error')
        setShowToast(true)
        setLoading(false)
        return
      }

      // Check if user is already a manager, seller, or delivery partner
      const { data: userProfile, error: userError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', newManager.userId)
        .single()

      if (userError) throw userError

      if (userProfile.role === 'manager') {
        setToastMessage('Cet utilisateur est déjà un manager')
        setToastType('error')
        setShowToast(true)
        setLoading(false)
        return
      }

      // Update user profile to manager role
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          role: 'manager',
          region: newManager.region
        })
        .eq('id', newManager.userId)

      if (updateError) throw updateError

      setToastMessage('Manager ajouté avec succès')
      setToastType('success')
      setShowToast(true)
      setShowAddManager(false)
      setNewManager({ userId: '', region: '' })
      fetchManagers()
    } catch (err) {
      console.error('Error adding manager:', err)
      setToastMessage('Erreur lors de l\'ajout du manager')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleManagerAction = async (managerId: string, action: 'block' | 'unblock' | 'delete') => {
    try {
      setLoading(true)

      if (action === 'delete') {
        // First, unassign all delivery partners
        const { error: unassignError } = await supabase
          .from('delivery_partners')
          .update({ manager_id: null })
          .eq('manager_id', managerId)

        if (unassignError) throw unassignError

        // Then delete the manager's profile
        const { error: deleteError } = await supabase
          .from('profiles')
          .delete()
          .eq('id', managerId)

        if (deleteError) throw deleteError

        setToastMessage('Manager et son groupe supprimés avec succès')
      } else {
        const status = action === 'block' ? 'blocked' : 'active'

        const { error: updateError } = await supabase
          .from('profiles')
          .update({ status })
          .eq('id', managerId)

        if (updateError) throw updateError

        setToastMessage(`Manager ${status === 'blocked' ? 'bloqué' : 'débloqué'} avec succès`)
      }

      setToastType('success')
      setShowToast(true)
      setShowManagerDetails(false)
      setShowDeleteConfirm(false)
      fetchManagers()
    } catch (err) {
      console.error('Error updating manager:', err)
      setToastMessage('Erreur lors de la mise à jour du manager')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleAssignPartner = async (partnerId: string) => {
    if (!selectedManager) return

    try {
      setLoading(true)

      const { error } = await supabase
        .from('delivery_partners')
        .update({ manager_id: selectedManager.id })
        .eq('id', partnerId)

      if (error) throw error

      setToastMessage('Livreur assigné avec succès')
      setToastType('success')
      setShowToast(true)
      setShowAssignPartner(false)
      fetchManagers()
    } catch (err) {
      console.error('Error assigning partner:', err)
      setToastMessage('Erreur lors de l\'assignation du livreur')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleUnassignPartner = async (partnerId: string) => {
    try {
      setLoading(true)

      const { error } = await supabase
        .from('delivery_partners')
        .update({ manager_id: null })
        .eq('id', partnerId)

      if (error) throw error

      setToastMessage('Livreur retiré avec succès')
      setToastType('success')
      setShowToast(true)
      fetchManagers()
    } catch (err) {
      console.error('Error unassigning partner:', err)
      setToastMessage('Erreur lors du retrait du livreur')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  // Helper function to check if a user can be assigned as manager
  const canBeManager = (userProfile: UserProfile) => {
    // Users who are already managers cannot be assigned again
    if (userProfile.role === 'manager') return false;
    
    // Return true for all other users
    return true;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Add Manager Modal */}
      {showAddManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Ajouter un Manager
              </h3>
              <button
                onClick={() => setShowAddManager(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sélectionner un utilisateur
                </label>
                <div className="mb-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      placeholder="Rechercher un utilisateur..."
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                    />
                  </div>
                </div>
                
                <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md">
                  {loadingUsers ? (
                    <div className="flex justify-center items-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  ) : availableUsers.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">Aucun utilisateur disponible</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {availableUsers.map(userProfile => (
                        <div 
                          key={userProfile.id}
                          className={`p-3 cursor-pointer hover:bg-gray-50 ${
                            !canBeManager(userProfile) ? 'opacity-50 cursor-not-allowed' : ''
                          } ${
                            newManager.userId === userProfile.id ? 'bg-primary/5 border-l-4 border-primary' : ''
                          }`}
                          onClick={() => {
                            if (canBeManager(userProfile)) {
                              setNewManager(prev => ({ ...prev, userId: userProfile.id }))
                            } else {
                              setToastMessage('Cet utilisateur ne peut pas être assigné comme manager')
                              setToastType('error')
                              setShowToast(true)
                            }
                          }}
                        >
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="h-5 w-5 text-gray-500" />
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">{userProfile.full_name || 'Sans nom'}</p>
                              <p className="text-xs text-gray-500">{userProfile.email}</p>
                              <div className="flex items-center mt-1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  userProfile.role === 'buyer' ? 'bg-blue-100 text-blue-800' :
                                  userProfile.role === 'seller' ? 'bg-green-100 text-green-800' :
                                  userProfile.role === 'delivery' ? 'bg-orange-100 text-orange-800' :
                                  userProfile.role === 'manager' ? 'bg-purple-100 text-purple-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {userProfile.role === 'buyer' ? 'Acheteur' : 
                                   userProfile.role === 'seller' ? 'Vendeur' :
                                   userProfile.role === 'delivery' ? 'Livreur' :
                                   userProfile.role === 'manager' ? 'Manager' : userProfile.role}
                                </span>
                                {!canBeManager(userProfile) && (
                                  <span className="ml-2 text-xs text-red-500">
                                    Non disponible
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Région
                </label>
                <input
                  type="text"
                  required
                  value={newManager.region}
                  onChange={(e) => setNewManager(prev => ({ ...prev, region: e.target.value }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowAddManager(false)}
                >
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  onClick={handleAddManager}
                  disabled={loading || !newManager.userId || !newManager.region}
                >
                  Ajouter le Manager
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manager Details Modal */}
      {showManagerDetails && selectedManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-gray-900">
                  Détails du Manager
                </h3>
                <button
                  onClick={() => setShowManagerDetails(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-6 space-y-6">
                {/* Manager Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Informations</h4>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-900">{selectedManager.full_name || 'Sans nom'}</p>
                      <p className="text-sm text-gray-500">{selectedManager.email}</p>
                    </div>
                    <div>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="h-4 w-4 mr-1" />
                        {selectedManager.region || 'Région non définie'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Performance Stats */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Performance</h4>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-lg text-white">
                      <div className="flex items-center justify-between">
                        <Activity className="h-6 w-6" />
                        <span className="text-xs font-medium">Score Global</span>
                      </div>
                      <div className="mt-2">
                        <div className="text-2xl font-bold">
                          {selectedManager.performance_score?.toFixed(1)}%
                        </div>
                        <div className="text-xs opacity-75">Performance globale</div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-lg text-white">
                      <div className="flex items-center justify-between">
                        <Target className="h-6 w-6" />
                        <span className="text-xs font-medium">Taux de Réussite</span>
                      </div>
                      <div className="mt-2">
                        <div className="text-2xl font-bold">
                          {selectedManager.success_rate?.toFixed(1)}%
                        </div>
                        <div className="text-xs opacity-75">Livraisons réussies</div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 rounded-lg text-white">
                      <div className="flex items-center justify-between">
                        <BarChart className="h-6 w-6" />
                        <span className="text-xs font-medium">Activité</span>
                      </div>
                      <div className="mt-2">
                        <div className="text-2xl font-bold">
                          {selectedManager.active_missions || 0}
                        </div>
                        <div className="text-xs opacity-75">Missions en cours</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Team Stats */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Statistiques de l'Équipe</h4>
                  <dl className="mt-2 grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-sm font-medium text-gray-500">
                        Livreurs Actifs
                      </dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">
                        {selectedManager.delivery_partners?.filter(p => p.status === 'active').length || 0}
                      </dd>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-sm font-medium text-gray-500">
                        Total Missions
                      </dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">
                        {selectedManager.total_missions || 0}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Delivery Partners */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-sm font-medium text-gray-500">Livreurs Assignés</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        fetchAvailablePartners()
                        setShowAssignPartner(true)
                      }}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Assigner un Livreur
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {selectedManager.delivery_partners?.map((partner) => (
                      <div
                        key={partner.id}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="h-5 w-5 text-gray-500" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">
                              {partner.applicant_name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {partner.applicant_email}
                            </p>
                            <div className="mt-1 flex items-center text-sm text-gray-500">
                              <Truck className="h-4 w-4 mr-1" />
                              <span className="capitalize">
                                {partner.vehicle_type === 'bike' ? 'Vélo' :
                                 partner.vehicle_type === 'motorcycle' ? 'Moto' :
                                 partner.vehicle_type === 'car' ? 'Voiture' :
                                 partner.vehicle_type === 'van' ? 'Camionnette' : 'À pied'}
                              </span>
                              <MapPin className="h-4 w-4 ml-2 mr-1" />
                              {partner.delivery_zone}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-900">
                              {partner.total_deliveries} livraisons
                            </div>
                            <div className="flex items-center text-sm text-gray-500">
                              <Star className="h-4 w-4 text-yellow-400 mr-1" />
                              {partner.rating?.toFixed(1) || '0.0'}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleUnassignPartner(partner.id)}
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    {!selectedManager.delivery_partners?.length && (
                      <div className="text-center py-8">
                        <Users className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">
                          Aucun livreur assigné
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Ce manager n'a pas encore de livreurs assignés
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-6 border-t flex justify-end space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowManagerDetails(false)}
                  >
                    Fermer
                  </Button>
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer
                  </Button>
                  {selectedManager.status === 'active' ? (
                    <Button
                      variant="outline"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleManagerAction(selectedManager.id, 'block')}
                    >
                      Bloquer
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={() => handleManagerAction(selectedManager.id, 'unblock')}
                    >
                      Débloquer
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Partner Modal */}
      {showAssignPartner && selectedManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full m-4">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Assigner un Livreur
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Sélectionnez un livreur à assigner à {selectedManager.full_name || 'ce manager'}
                </p>
              </div>
              <button
                onClick={() => setShowAssignPartner(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {availablePartners.map((partner) => (
                <div
                  key={partner.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="h-5 w-5 text-gray-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {partner.applicant_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {partner.applicant_email}
                      </p>
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <Truck className="h-4 w-4 mr-1" />
                        <span className="capitalize">
                          {partner.vehicle_type === 'bike' ? 'Vélo' :
                           partner.vehicle_type === 'motorcycle' ? 'Moto' :
                           partner.vehicle_type === 'car' ? 'Voiture' :
                           partner.vehicle_type === 'van' ? 'Camionnette' : 'À pied'}
                        </span>
                        <MapPin className="h-4 w-4 ml-2 mr-1" />
                        {partner.delivery_zone}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleAssignPartner(partner.id)}
                  >
                    Assigner
                  </Button>
                </div>
              ))}

              {availablePartners.length === 0 && (
                <div className="text-center py-8">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    Aucun livreur disponible
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Il n'y a pas de livreurs actifs non assignés pour le moment
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mx-auto">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>

            <div className="mt-3 text-center sm:mt-5">
              <h3 className="text-lg font-medium text-gray-900">
                Confirmer la suppression
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Êtes-vous sûr de vouloir supprimer ce manager et son groupe ? Cette action est irréversible et tous les livreurs seront désassignés.
                </p>
              </div>
            </div>

            <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Annuler
              </Button>
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={() => handleManagerAction(selectedManager.id, 'delete')}
              >
                Supprimer
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="mr-4"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Gestion des Départements
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Gérez les managers départementaux et leurs équipes de livraison
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            onClick={() => setShowAddManager(true)}
          >
            <Plus className="h-5 w-5 mr-2" />
            Ajouter un Manager
          </Button>
        </div>

        {/* Performance Overview */}
        <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-white/80 truncate">
                      Total Managers
                    </dt>
                    <dd className="text-lg font-semibold text-white">
                      {managers.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Activity className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-white/80 truncate">
                      Taux de Réussite Moyen
                    </dt>
                    <dd className="text-lg font-semibold text-white">
                      {managers.length > 0
                        ? (managers.reduce((sum, m) => sum + (m.success_rate || 0), 0) / managers.length).toFixed(1)
                        : '0.0'}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-white/80 truncate">
                      Performance Moyenne
                    </dt>
                    <dd className="text-lg font-semibold text-white">
                      {managers.length > 0
                        ? (managers.reduce((sum, m) => sum + (m.performance_score || 0), 0) / managers.length).toFixed(1)
                        : '0.0'}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Package className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-white/80 truncate">
                      Missions Actives
                    </dt>
                    <dd className="text-lg font-semibold text-white">
                      {managers.reduce((sum, m) => sum + (m.active_missions || 0), 0)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher un manager..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : managers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun manager</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Commencez par ajouter un manager départemental
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Manager
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Région
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Performance
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Équipe
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Missions
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {managers.map((manager) => (
                      <tr key={manager.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <User className="h-5 w-5 text-gray-500" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {manager.full_name || 'Sans nom'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {manager.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <MapPin className="h-5 w-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {manager.region || 'Région non définie'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <div className="text-sm font-medium text-gray-900">
                                  {manager.performance_score?.toFixed(1)}%
                                </div>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                <div
                                  className="bg-primary rounded-full h-2"
                                  style={{ width: `${manager.performance_score || 0}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {manager.delivery_partners?.length || 0} livreurs
                          </div>
                          <div className="text-sm text-gray-500">
                            {manager.delivery_partners?.filter(p => p.status === 'active').length || 0} actifs
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {manager.active_missions || 0} en cours
                          </div>
                          <div className="text-sm text-gray-500">
                            {manager.total_missions || 0} total
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            manager.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {manager.status === 'active' ? (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            ) : (
                              <XCircle className="h-3 w-3 mr-1" />
                            )}
                            {manager.status === 'active' ? 'Actif' : 'Bloqué'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedManager(manager)
                              setShowManagerDetails(true)
                            }}
                          >
                            Détails
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}